// Number/currency formatters, transliterated from the original inline scripts.
//
// Provenance:
//   money / moneyBig / num  — docs/discovery-calculator.dc.html:1479-1481 AND
//                             docs/value-story-deck.dc.html:245-247. Diffed: the two
//                             implementations are byte-identical, so there is a single
//                             export for each (no `moneyDeck` variant is needed).
//   fmtNum                  — calculator only (discovery-calculator.dc.html:1306-1310).
//                             Formats in-flight numeric input text; pins 'en-US' explicitly.
//   hintFor                 — calculator only (discovery-calculator.dc.html:1664).
//   fmtInput                — deck only (value-story-deck.dc.html:248). Renders a captured
//                             story input by its question kind.
//
// `money`/`moneyBig`/`num` call `.toLocaleString()` with no locale argument, exactly as the
// source did — the grouping therefore follows the host default locale (en-US in the browser
// the goldens were captured in, and in bun).
//
// NOT transliterated — the ROI arithmetic at the foot of this file:
//   returnMultiple / returnMultipleText  — extracted from two existing inline call sites
//                             that shared one rule (CalculatorApp's `roiRatioText` and
//                             ValueStoryDeck's `ratioText`). Behaviour-identical to both,
//                             so no golden fixture is affected.
//   paybackMonths / paybackText  — genuinely new. Nothing in the engine or the story
//                             snapshot computes a payback period; it is derived here from
//                             the snapshot's `clayCost` and `attributedValue`.

import type { QuestionKind } from '../types';

/** money() — compact currency. $1M drops a trailing `.0`; $1.2M keeps one decimal. */
export function money(v: number): string {
  if (!isFinite(v)) v = 0;
  const n = Math.round(v),
    a = Math.abs(n);
  if (a >= 1e6) return '$' + (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (a >= 1e3) return '$' + Math.round(n / 1e3) + 'K';
  return '$' + n.toLocaleString();
}

/** moneyBig() — same as money() but always keeps the millions decimal ($1.0M). */
export function moneyBig(v: number): string {
  if (!isFinite(v)) v = 0;
  const n = Math.round(v),
    a = Math.abs(n);
  if (a >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
  if (a >= 1e3) return '$' + Math.round(n / 1e3) + 'K';
  return '$' + n.toLocaleString();
}

/** num() — rounded, thousands-separated integer. Non-finite input yields 'NaN', as in source. */
export function num(v: number): string {
  return Math.round(v).toLocaleString();
}

/**
 * fmtNum() — thousands-separates numeric *input text* without fighting the caret. Anything
 * that is not a plain (optionally signed / decimal) number string is passed through verbatim.
 */
export function fmtNum(v: number | string | null | undefined): string {
  if (v === '' || v == null) return '';
  const m = /^(-?)(\d*)(\.\d*)?$/.exec(String(v));
  if (!m) return String(v);
  return m[1] + (m[2] ? Number(m[2]).toLocaleString('en-US') : '') + (m[3] || '');
}

/** hintFor() — the "(typical: …)" hint rendered next to a gather question. */
export function hintFor(kind: QuestionKind | string, def: number): string {
  if (kind === 'money' || kind === 'rate')
    return '$' + Number(def).toLocaleString() + (kind === 'rate' ? '/hr' : '');
  if (kind === 'pct') return def + '%';
  if (kind === 'min') return def + ' min';
  return Number(def).toLocaleString();
}

/** fmtInput() — deck-side rendering of a captured story input value. */
export function fmtInput(kind: string, v: number | string | null | undefined): string {
  const n = Number(v);
  if (!isFinite(n)) return String(v ?? '—');
  if (kind === 'money') return '$' + n.toLocaleString();
  if (kind === 'pct') return n + '%';
  if (kind === 'min') return n + ' min';
  if (kind === 'hrs') return n + ' hrs';
  return n.toLocaleString();
}

/* ══════════════════════════════════════════════════════════════════
   ROI ARITHMETIC — shared by the calculator and the deck
   ══════════════════════════════════════════════════════════════════

   These live in format.ts rather than engine.ts on purpose: the deck imports
   only format.ts and migrate.ts (never engine.ts — the story snapshot is the
   boundary), so this is the one module both surfaces can reach. Before this,
   the return multiple was computed inline in two places with the same rule —
   CalculatorApp's `roiRatioText` and ValueStoryDeck's `ratioText`. */

/**
 * returnMultiple() — attributed value measured against the Clay investment.
 * `null` means the question has no answer: with no investment there is no ROI,
 * so callers render their own placeholder rather than an Infinity.
 */
export function returnMultiple(attributedValue: number, clayCost: number): number | null {
  if (!isFinite(attributedValue) || !isFinite(clayCost)) return null;
  if (clayCost <= 0) return null;
  return attributedValue / clayCost;
}

/** returnMultipleText() — `6.4×` / `168×`. Pins the rule both call sites already used. */
export function returnMultipleText(attributedValue: number, clayCost: number): string | null {
  const r = returnMultiple(attributedValue, clayCost);
  if (r == null) return null;
  return (r >= 10 ? Math.round(r) : r.toFixed(1)) + '×';
}

/**
 * paybackMonths() — months of attributed value needed to recoup the Clay cost.
 * Derived, not stored: nothing in the engine or the story snapshot computes it.
 * `null` when either side is missing — a story with no attributed value never
 * pays back, and must not render as `Infinity`.
 */
export function paybackMonths(attributedValue: number, clayCost: number): number | null {
  if (!isFinite(attributedValue) || !isFinite(clayCost)) return null;
  if (attributedValue <= 0 || clayCost <= 0) return null;
  return clayCost / (attributedValue / 12);
}

/** paybackText() — `<1 month` / `1 month` / `1.9 months` / `15 months`. */
export function paybackText(attributedValue: number, clayCost: number): string | null {
  const m = paybackMonths(attributedValue, clayCost);
  if (m == null) return null;
  if (m < 1) return '<1 month';
  const n = m >= 10 ? Math.round(m) : Number(m.toFixed(1));
  return n === 1 ? '1 month' : `${n} months`;
}
